import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Select, Space, Tag } from "antd";

import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";

import { useNavigate } from "react-router-dom";

import { getInstitutions } from "../../services/institutionService";
import { getInstitutionCategories } from "../../services/institutionCategory";

import { getFocusAreas } from "../../services/focusAreaService";
import { getOwnerships } from "../../services/ownershipService";
import { getLicenseCategories } from "../../services/licenseCategoryService";

import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";
import { districts } from "../../utils/districts";
import { utils, writeFile } from "xlsx";

const InstitutionsReport = () => {
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [institutionCategories, setInstitutionCategories] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Not Selected");

  const [selectedFocusArea, setSelectedFocusArea] = useState("Not Selected");

  const [ownershipCategories, setOwnershipCategories] = useState([]);
  const [licensingCategories, setLicensingCategories] = useState([]);

  const [selectedOwnershipCategory, setSelectedOwnershipCategory] =
    useState("Not Selected");
  const [selectedLicensingCategory, setSelectedLicensingCategory] =
    useState("Not Selected");

  const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] =
    useState("All");

  const [selectedLocation, setSelectedLocation] = useState("Not Selected");

  const [selectedRegion, setSelectedRegion] = useState("Not Selected");

  useEffect(() => {
    loadInstitutions();
    loadCategories();
    loadfocusAreas();
    loadOwnershipCategories();
    loadLicensingCategories();
  }, []);

  useEffect(() => {
    let filtered = institutions;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.category?.id?.toString() === selectedCategory.toString()
      );
    }

    if (selectedOwnershipCategory !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.ownership?.id?.toString() ===
          selectedOwnershipCategory.toString()
      );
    }

    if (selectedLicensingCategory !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.license_category?.id?.toString() ===
          selectedLicensingCategory.toString()
      );
    }

    if (selectedFocusArea !== "Not Selected") {
      filtered = filtered.filter((item) =>
        item.focus_areas?.some(
          (area) => area.id?.toString() === selectedFocusArea.toString()
        )
      );
    }

    if (selectedSubscriptionStatus === "Expired") {
      filtered = filtered.filter((item) => {
        const today = new Date();
        const expiry = item.subscription_expiry_date
          ? new Date(item.subscription_expiry_date)
          : null;

        return expiry && expiry < today;
      });
    }

    if (selectedSubscriptionStatus === "Active") {
      filtered = filtered.filter((item) => {
        const today = new Date();
        const expiry = item.subscription_expiry_date
          ? new Date(item.subscription_expiry_date)
          : null;

        return expiry && expiry >= today;
      });
    }

    if (selectedLocation !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.location?.toLowerCase() === selectedLocation.toLowerCase()
      );
    }

    if (selectedRegion !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.region?.toLowerCase() === selectedRegion.toLowerCase()
      );
    }

    setFilteredInstitutions(filtered);
  }, [
    searchTerm,
    institutions,
    selectedCategory,
    selectedOwnershipCategory,
    selectedFocusArea,
    selectedLicensingCategory,
    selectedSubscriptionStatus,
    selectedLocation,
    selectedRegion,
  ]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutionCategories();
      setInstitutionCategories(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const loadOwnershipCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getOwnerships();
      setOwnershipCategories(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to ownership categories"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadLicensingCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getLicenseCategories();
      setLicensingCategories(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to licensing categories"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadfocusAreas = async () => {
    setLoading(true);
    try {
      const { data } = await getFocusAreas();
      setFocusAreas(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load focus areas");
    } finally {
      setLoading(false);
    }
  };

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
      setFilteredInstitutions(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Institutions"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const title = "Institution Report";
    const fileName = "institution-report.pdf";

    const columns = [
      "Name",
      "District",
      "Institution Category",
      "Licensing Category",
      "Ownership Type",
      "Focus Areas",
      "VC/Principal",
      "Status",
      "Subscription Expiry",
      "Subscription Status",
    ];

    const rows = filteredInstitutions.map((inst) => [
      inst.name,
      inst.location,
      inst.category?.name || "N/A",
      inst.license_category?.name || "N/A",
      inst.ownership?.name || "N/A",
      inst.focus_areas?.map((fa) => fa.name).join(", ") || "None",
      inst.vc_name || "N/A",
      inst.membership_status ? "Active" : "Inactive",
      inst.subscription_expiry_date || "—",
      (() => {
        const today = new Date();
        const expiry = inst.subscription_expiry_date
          ? new Date(inst.subscription_expiry_date)
          : null;
        if (!expiry) return "No Date";
        return expiry < today ? "Expired" : "Active";
      })(),
    ]);

    generatePDF({
      title,
      columns,
      rows,
      fileName,
      orientation: "landscape",
    });
  };

  // NEW: Excel export
  const handleDownloadExcel = () => {
    const columns = [
      "Name",
      "Code",
      "Year of Establishment",
      "District",
      "Region",
      "Contact",
      "Email",
      "Institution Category",
      "Licensing Category",
      "Ownership Type",
      "Focus Areas",
      "VC/Principal",
      "Status",
      "Subscription Expiry",
      "Subscription Status",
    ];

    const rows = filteredInstitutions.map((inst) => [
      inst.name,
      inst.code,
      inst.year_of_establishment || "N/A",
      inst.location,
      inst.region || "N/A",
      inst.contact || "N/A",
      inst.email || "N/A",
      inst.category?.name || "N/A",
      inst.license_category?.name || "N/A",
      inst.ownership?.name || "N/A",
      inst.focus_areas?.map((fa) => fa.name).join(", ") || "None",
      inst.vc_name || "N/A",
      inst.membership_status ? "Active" : "Inactive",
      inst.subscription_expiry_date || "—",
      (() => {
        const today = new Date();
        const expiry = inst.subscription_expiry_date
          ? new Date(inst.subscription_expiry_date)
          : null;
        if (!expiry) return "No Date";
        return expiry < today ? "Expired" : "Active";
      })(),
    ]);

    const ws = utils.aoa_to_sheet([columns, ...rows]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Institution Report");
    writeFile(wb, "institution-report.xlsx");
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 150,
    },
    {
      title: "District",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Institution Category",
      dataIndex: "category",
      key: "category",
      render: (_, record) => (
        <Tag color="magenta" className="ml-1">
          {record.category?.name || "N/A"}
        </Tag>
      ),
      width: 180,
    },

    {
      title: "Licensing Category",
      dataIndex: "license_category",
      key: "license_category",
      render: (_, record) => (
        <Tag color="blue" className="ml-1">
          {record.license_category?.name || "N/A"}
        </Tag>
      ),
      width: 180,
    },

    {
      title: "Ownership Type",
      dataIndex: "ownership",
      key: "ownership",
      render: (_, record) => (
        <Tag color="cyan" className="ml-1">
          {record.ownership?.name || "N/A"}
        </Tag>
      ),
      width: 180,
    },

    {
      title: "Focus Areas",
      dataIndex: "focus_areas",
      key: "focus_areas",
      width: 300,
      render: (focusAreas) =>
        focusAreas && focusAreas.length > 0 ? (
          <Space wrap>
            {focusAreas.map((area) => (
              <Tag key={area.id} color="blue">
                {area.name}
              </Tag>
            ))}
          </Space>
        ) : (
          <span className="text-gray-400 italic">None</span>
        ),
    },
    {
      title: "VC/Principal",
      dataIndex: "vc_name",
      key: "vc_name",
    },
    {
      title: "Status",
      render: (_, record) => (
        <Tag color={record.membership_status ? "green" : "red"}>
          {record.membership_status ? "Active" : "Inactive"}
        </Tag>
      ),
    },

    {
      title: "Subscription Expiry",
      dataIndex: "subscription_expiry_date",
      key: "subscription_expiry_date",
    },

    {
      title: "Subscription Status",
      key: "subscription_status",
      render: (_, record) => {
        const today = new Date();
        const expiry = record.subscription_expiry_date
          ? new Date(record.subscription_expiry_date)
          : null;

        if (!expiry) return <Tag color="default">No Date</Tag>;

        return expiry < today ? (
          <Tag color="red">Expired</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        );
      },
    },
  ];

  const uniqueRegions = districts.reduce((acc, curr) => {
    if (!acc.includes(curr.region)) {
      acc.push(curr.region);
    }
    return acc;
  }, []);

  return (
    <>
      <PageHeading title="Institutions" subtitle1="Institutions" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Institution List"
            count={filteredInstitutions.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredInstitutions.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="grid gap-y-4 gap-x-3 grid-cols-1 md:grid-cols-5">
                <Select
                  multiple={false}
                  id="categories"
                  defaultValue={selectedCategory}
                  className="w-full h-[36px]"
                  onChange={(value) => {
                    setSelectedCategory(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Category
                  </Select.Option>
                  {institutionCategories &&
                    institutionCategories.length > 0 &&
                    institutionCategories.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>

                <Select
                  multiple={false}
                  defaultValue={selectedFocusArea}
                  className="w-full h-[36px]"
                  onChange={(value) => setSelectedFocusArea(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Focus Area
                  </Select.Option>
                  {focusAreas.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.name}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  multiple={false}
                  defaultValue={selectedLicensingCategory}
                  className="w-full h-[36px]"
                  onChange={(value) => setSelectedLicensingCategory(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Licensing Category
                  </Select.Option>
                  {licensingCategories &&
                    licensingCategories.length > 0 &&
                    licensingCategories.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>

                <Select
                  multiple={false}
                  id="ownership"
                  defaultValue={selectedOwnershipCategory}
                  className="w-full h-[36px]"
                  onChange={(value) => {
                    setSelectedOwnershipCategory(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Ownership
                  </Select.Option>
                  {ownershipCategories &&
                    ownershipCategories.length > 0 &&
                    ownershipCategories.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>

                <Select
                  defaultValue={selectedSubscriptionStatus}
                  className="w-full h-[36px]"
                  onChange={(value) => setSelectedSubscriptionStatus(value)}
                >
                  <Select.Option value="All">
                    Filter by Subscription
                  </Select.Option>
                  <Select.Option value="Active">
                    Active Subscriptions
                  </Select.Option>
                  <Select.Option value="Expired">
                    Expired Subscriptions
                  </Select.Option>
                </Select>

                <Select
                  defaultValue={selectedLocation}
                  className="w-full h-[36px]"
                  onChange={(value) => setSelectedLocation(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Location
                  </Select.Option>
                  {districts.map((district) => (
                    <Select.Option key={district.id} value={district.name}>
                      {district.name}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  defaultValue={selectedRegion}
                  className="w-full h-[36px]"
                  onChange={(value) => setSelectedRegion(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="Not Selected">
                    Filter by Region
                  </Select.Option>
                  {uniqueRegions.map((region, index) => (
                    <Select.Option key={index} value={region}>
                      {region}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            }
          />

          <div className="mt-4 flex gap-2 flex-wrap">
            <DownloadButton
              onDownload={handleDownloadPDF}
              title="Download PDF"
            />
            <DownloadButton
              onDownload={handleDownloadExcel}
              title="Download Excel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default InstitutionsReport;
